import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Menu } from 'primereact/menu';
import { Paginator } from 'primereact/paginator';
import { Dialog } from 'primereact/dialog';
import { useDebounce } from 'use-debounce';
import CustomToast from '../../components/CustomToast';
import JobHistory from './JobHistory';
import {
  PERM,
  JOB_STATUS,
  JOB_STATUS_ACTION,
  apiErrorMessage,
  experienceLabel,
  formatDate,
  linkedInShareUrl,
  portalJobUrl,
  useCareersPermissions,
  useCareersRequest,
} from './careersShared';
import {
  CareersPage, EmptyState, ErrorBanner, JobStatusBadge, Loading, PageHeader, SearchBox,
} from './careersUI';
import './careers.css';

const PAGE_SIZE = 20;

const STATUS_FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Draft', value: 'draft' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Closed', value: 'closed' },
];

// Confirmation text for status changes that take a job off the portal
const STATUS_CONFIRM = {
  inactive: 'The job will be hidden from the careers portal until you activate it again. Applications are kept.',
  closed: 'The job will be removed from the careers portal and can no longer be edited unless you reopen it. Applications are kept.',
};

const statusActionLabel = (job, next) => (job.status === 'closed' && next === 'active' ? 'Reopen' : JOB_STATUS_ACTION[next].label);

const JobPostings = () => {
  const request = useCareersRequest();
  const can = useCareersPermissions();
  const navigate = useNavigate();
  const toastRef = useRef(null);
  const menuRef = useRef(null);

  const [status, setStatus] = useState('all');
  const [categoryId, setCategoryId] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 350);
  const [page, setPage] = useState(1);

  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busyJobId, setBusyJobId] = useState(null);
  const [historyJob, setHistoryJob] = useState(null);
  const [menuJob, setMenuJob] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const notify = (severity, detail) => toastRef.current?.show(severity, severity === 'error' ? 'Error' : 'Done', detail);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await request('/jobs', {
        params: { status, category_id: categoryId || undefined, search: debouncedSearch || undefined, page, pageSize: PAGE_SIZE },
      });
      setJobs(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      setLoadError(apiErrorMessage(error, 'Could not load job postings.'));
    } finally {
      setLoading(false);
    }
  }, [request, status, categoryId, debouncedSearch, page]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  useEffect(() => {
    request('/categories')
      .then((data) => setCategories(data.categories || []))
      .catch(() => setCategories([]));
  }, [request]);

  // Any filter change goes back to the first page
  useEffect(() => { setPage(1); }, [status, categoryId, debouncedSearch]);

  const runAction = async (job, action, successMessage) => {
    setBusyJobId(job.job_id);
    try {
      await action();
      notify('success', successMessage);
      await loadJobs();
    } catch (error) {
      notify('error', apiErrorMessage(error));
    } finally {
      setBusyJobId(null);
    }
  };

  const changeStatus = (job, next) => {
    const doIt = () => runAction(
      job,
      () => request(`/jobs/${job.job_id}/status`, { method: 'patch', data: { status: next } }),
      `${job.title} is now ${JOB_STATUS[next].label.toLowerCase()}.`,
    );
    if (STATUS_CONFIRM[next]) {
      setConfirm({
        title: `${JOB_STATUS_ACTION[next].label} "${job.title}"?`,
        message: STATUS_CONFIRM[next],
        acceptLabel: JOB_STATUS_ACTION[next].label,
        onAccept: doIt,
      });
    } else {
      doIt();
    }
  };

  const duplicate = (job) => runAction(job, async () => {
    const data = await request(`/jobs/${job.job_id}/duplicate`, { method: 'post' });
    navigate(`/Graphe/dashboard/careers/jobs/${data.job.job_id}/edit`);
  }, 'A draft copy was created.');

  const remove = (job) => setConfirm({
    title: `Delete "${job.title}"?`,
    message: 'The job will be removed from this list. Its applications and history are kept.',
    acceptLabel: 'Delete',
    danger: true,
    onAccept: () => runAction(job, () => request(`/jobs/${job.job_id}`, { method: 'delete' }), 'Job deleted.'),
  });

  const canManage = can(PERM.manageJobs);
  const canPublish = can(PERM.publishJobs);
  const canReadApplications = can(PERM.readApplications);
  const categoryOptions = categories.map((c) => ({ label: c.is_active ? c.name : `${c.name} (inactive)`, value: c.category_id }));
  const hasFilters = status !== 'all' || Boolean(search) || Boolean(categoryId);
  const editPath = (job) => `/Graphe/dashboard/careers/jobs/${job.job_id}/edit`;

  const menuItems = (job) => {
    if (!job) return [];
    const items = [];
    if (canPublish) {
      job.allowed_statuses.forEach((next) => items.push({
        label: statusActionLabel(job, next),
        icon: JOB_STATUS_ACTION[next].icon,
        command: () => changeStatus(job, next),
      }));
    }
    if (canManage) items.push({ label: 'Duplicate', icon: 'pi pi-copy', command: () => duplicate(job) });
    items.push({ label: 'History', icon: 'pi pi-history', command: () => setHistoryJob(job) });
    if (canManage && job.status !== 'active') {
      items.push({ separator: true });
      items.push({ label: 'Delete', icon: 'pi pi-trash', className: 'careers-menu-danger', command: () => remove(job) });
    }
    return items;
  };

  const openMenu = (event, job) => {
    setMenuJob(job);
    menuRef.current?.toggle(event);
  };

  return (
    <CareersPage>
      <CustomToast ref={toastRef} />
      <PageHeader
        title="Job Postings"
        subtitle="Create roles, publish them to the careers portal, and track applicants."
        actions={(
          <>
            {can(PERM.manageSettings) && (
              <Button
                label="Categories"
                icon="pi pi-tags"
                severity="secondary"
                outlined
                onClick={() => navigate('/Graphe/dashboard/careers/categories')}
              />
            )}
            {canManage && (
              <Button label="New Job" icon="pi pi-plus" severity="help" onClick={() => navigate('/Graphe/dashboard/careers/jobs/new')} />
            )}
          </>
        )}
      />

      <div className="careers-card is-flush">
        <div className="careers-toolbar">
          <div className="careers-tabs" role="group" aria-label="Filter by status">
            {STATUS_FILTERS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={status === option.value ? 'is-active' : ''}
                aria-pressed={status === option.value}
                onClick={() => setStatus(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <SearchBox value={search} onChange={setSearch} placeholder="Search title or location" label="Search jobs" />
          <Dropdown
            value={categoryId}
            options={categoryOptions}
            onChange={(e) => setCategoryId(e.value)}
            placeholder="All categories"
            showClear
            aria-label="Filter by category"
          />
        </div>

        <ErrorBanner message={loadError} onRetry={loadJobs} />

        {loading && !jobs.length ? (
          <Loading />
        ) : !jobs.length && !loadError ? (
          <EmptyState icon="pi-briefcase" title={hasFilters ? 'No job postings match these filters' : 'No job postings yet'}>
            {canManage && !hasFilters && (
              <Button label="Post your first job" icon="pi pi-plus" severity="help" onClick={() => navigate('/Graphe/dashboard/careers/jobs/new')} />
            )}
          </EmptyState>
        ) : (
          <div className="careers-table-wrap">
            <table className="careers-table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Status</th>
                  <th>Applicants</th>
                  <th>Posted</th>
                  <th>Deadline</th>
                  <th className="is-actions"><span className="visually-hidden">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => {
                  const busy = busyJobId === job.job_id;
                  const newCount = job.applications?.byStatus?.applied || 0;
                  const applicants = job.applications?.total || 0;
                  return (
                    <tr key={job.job_id}>
                      <td style={{ minWidth: 280 }}>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          {canManage ? (
                            <Link to={editPath(job)} className="careers-primary-text text-decoration-none">{job.title}</Link>
                          ) : (
                            <span className="careers-primary-text">{job.title}</span>
                          )}
                          {job.is_urgent && <span className="careers-badge is-urgent">Urgent</span>}
                        </div>
                        <div className="careers-meta-row">
                          <span><i className="pi pi-folder" aria-hidden="true" />{job.category?.name || 'No category'}</span>
                          <span><i className="pi pi-map-marker" aria-hidden="true" />{job.location}</span>
                          <span><i className="pi pi-clock" aria-hidden="true" />{job.employment_type}</span>
                          <span><i className="pi pi-chart-line" aria-hidden="true" />{experienceLabel(job.experience_min, job.experience_max)}</span>
                          <span><i className="pi pi-users" aria-hidden="true" />{job.vacancies} {job.vacancies === 1 ? 'opening' : 'openings'}</span>
                        </div>
                      </td>
                      <td>
                        <JobStatusBadge status={job.status} />
                        {job.status === 'active' && !job.is_public && (
                          <span className="careers-warning-note">Not on the portal: deadline passed</span>
                        )}
                      </td>
                      <td className="is-num">
                        {canReadApplications ? (
                          <Link
                            to={`/Graphe/dashboard/careers/applications?job_id=${job.job_id}`}
                            className="careers-count-link"
                            aria-label={`${applicants} applications for ${job.title}`}
                          >
                            {applicants}
                          </Link>
                        ) : <span className="careers-count-link">{applicants}</span>}
                        {newCount > 0 && <span className="careers-new-pill">{newCount} new</span>}
                      </td>
                      <td className="is-num">{formatDate(job.published_at)}</td>
                      <td className="is-num">{formatDate(job.application_deadline)}</td>
                      <td className="is-actions">
                        <div className="careers-row-actions">
                          {canManage && (
                            <Button
                              tooltip={job.status === 'closed' ? 'View' : 'Edit'}
                              tooltipOptions={{ position: 'top' }}
                              icon={job.status === 'closed' ? 'pi pi-eye' : 'pi pi-pencil'}
                              aria-label={`${job.status === 'closed' ? 'View' : 'Edit'} ${job.title}`}
                              severity="help"
                              text
                              size="small"
                              disabled={busy}
                              onClick={() => navigate(editPath(job))}
                              className="py-1"
                            />
                          )}
                          {job.is_public && (
                            <a
                              href={portalJobUrl(job.slug)}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="View on careers portal"
                              aria-label={`View ${job.title} on the careers portal`}
                              className="p-button p-button-text p-button-sm p-button-secondary p-button-icon-only py-1"
                            >
                              <i className="pi pi-external-link" />
                            </a>
                          )}
                          {job.is_public && (
                            <a
                              href={linkedInShareUrl(job.slug)}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Post on LinkedIn"
                              aria-label={`Post ${job.title} on LinkedIn`}
                              className="p-button p-button-text p-button-sm p-button-secondary p-button-icon-only py-1"
                            >
                              <i className="pi pi-linkedin" />
                            </a>
                          )}
                          <Button
                            icon="pi pi-ellipsis-v"
                            text
                            rounded
                            severity="secondary"
                            title="More actions"
                            aria-label={`More actions for ${job.title}`}
                            aria-haspopup="menu"
                            loading={busy}
                            onClick={(e) => openMenu(e, job)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {total > 0 && (
          <div className="careers-table-footer">
            <span>{total} {total === 1 ? 'job' : 'jobs'}</span>
            {total > PAGE_SIZE && (
              <Paginator
                first={(page - 1) * PAGE_SIZE}
                rows={PAGE_SIZE}
                totalRecords={total}
                onPageChange={(e) => setPage(e.page + 1)}
              />
            )}
          </div>
        )}
      </div>

      <Menu ref={menuRef} model={menuItems(menuJob)} popup className="careers-menu" />

      <JobHistory job={historyJob} categories={categories} onHide={() => setHistoryJob(null)} />

      <Dialog
        header={confirm?.title}
        visible={Boolean(confirm)}
        style={{ width: '28rem', maxWidth: '95vw' }}
        className="careers-dialog"
        modal
        onHide={() => setConfirm(null)}
        footer={(
          <div>
            <Button label="Cancel" text severity="secondary" onClick={() => setConfirm(null)} />
            <Button
              label={confirm?.acceptLabel}
              severity={confirm?.danger ? 'danger' : 'warning'}
              onClick={() => {
                const action = confirm.onAccept;
                setConfirm(null);
                action();
              }}
              autoFocus
            />
          </div>
        )}
      >
        <p className="mb-0">{confirm?.message}</p>
      </Dialog>
    </CareersPage>
  );
};

export default JobPostings;
